import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Label, TextField, Radio, RadioItem, Button } from 'mw-style-react';
import { GET_REF_BOOK } from '@control-front-end/common/constants/refBooks';
import { GET_LAYER_INFO } from '@control-front-end/common/constants/graphLayers';
import {
  GET_ACTOR_BY_IDS,
  GET_FILTER,
} from '@control-front-end/common/constants/graphActors';
import { URL_REGEXP } from '@control-front-end/common/constants/regExp';
import SelectRefBook from '@control-front-end/common/components/SelectRefBook';
import SelectLayer from '@control-front-end/common/components/SelectLayer';
import SelectActors from '@control-front-end/common/components/SelectActors';
import SelectActorFilters from '@control-front-end/common/components/SelectActorFilters';
import ActorsFiltersPopup from '@control-front-end/common/components/ActorsFiltersPopup';
import useIntl from 'useIntl';
import mes from './intl';
import './OptionsSource.scss';

export const SOURCES = [
  { value: 'manual', title: 'Manual' },
  { value: 'layer', title: 'Graph Layer' },
  { value: 'actorFilter', title: 'Actor type' },
  { value: 'actors', title: 'Separate actors' },
  { value: 'refBook', title: 'Ref. book' },
  { value: 'api', title: 'API' },
  { value: 'corezoidSyncApi', title: 'Corezoid Sync API' },
];

/**
 * Компонент выбора источника данных для Select, Radio, MultiSelect
 */
function OptionsSource({
  id,
  isActive,
  extra = {},
  errors = {},
  onChange,
  handleInitOptions,
}) {
  const t = useIntl();
  const { optionsSource = { type: 'manual', value: null } } = extra;
  const [valueTitle, setValueTitle] = useState({});
  const [arrValueTitle, setArrValueTitle] = useState([]);
  const dispatch = useDispatch();

  const errorCallback = (errId) => {
    setValueTitle({ id: null, title: t(mes.accessDenied) });
    const newErrors = errors || {};
    newErrors.id = errId;
    onChange({ fieldId: 'extra', id, value: extra, errors: newErrors });
  };

  useEffect(() => {
    const { optionsSource: source } = extra;
    if (!source || !source.value || !source.type || source.type === 'manual')
      return;
    switch (source.type) {
      case 'refBook':
        dispatch({
          type: GET_REF_BOOK.REQUEST,
          payload: source.value.id,
          callback: (refBook) =>
            setValueTitle({ id: refBook.id, title: refBook.title }),
          errorCallback: () => errorCallback(t(mes.refBookAccessDenied)),
        });
        break;
      case 'layer':
        dispatch({
          type: GET_LAYER_INFO.REQUEST,
          payload: source.value.id,
          callback: (layer) =>
            setValueTitle({ id: layer.id, name: layer.name }),
          errorCallback: () => errorCallback(t(mes.layerSourceAccessDenied)),
        });
        break;
      case 'actors':
        dispatch({
          type: GET_ACTOR_BY_IDS.REQUEST,
          payload: source.value.ids,
          callback: (actors) => setArrValueTitle(actors),
        });
        break;
      case 'actorFilter':
        dispatch({
          type: GET_FILTER.REQUEST,
          payload: source.value.id,
          callback: (filter) =>
            setValueTitle({ id: filter.id, name: filter.name }),
          errorCallback: () => errorCallback(t(mes.filterAccessDenied)),
        });
        break;
      default:
    }
  }, []);

  // Смена типа источника
  const handleSourceTypeChange = ({ value }) => {
    const newExtra = { ...extra };
    newExtra.optionsSource = { type: value, value: null };
    onChange({ fieldId: 'extra', id, value: newExtra });
    setValueTitle({});
    setArrValueTitle([]);
    if (value === 'manual') {
      handleInitOptions();
    }
  };

  // Смена значения источника (refBookId, templateId, URL, ...)
  const handleChangeValue = ({ id: fieldId, value, error }) => {
    const newExtra = { ...extra };
    newExtra.optionsSource = extra.optionsSource || {};
    if (!newExtra.optionsSource.value) {
      newExtra.optionsSource.value = { [fieldId]: value };
    } else {
      newExtra.optionsSource.value[fieldId] = value;
    }
    const newErrors = errors || {};
    if (error) {
      newErrors[fieldId] = t(mes[`optSourceError_${fieldId}`]);
    } else {
      delete newErrors.id;
      delete newErrors[fieldId];
    }
    onChange({ fieldId: 'extra', id, value: newExtra, errors: newErrors });
  };

  const renderSourceSettings = () => {
    const { type = 'manual', value = {} } = optionsSource;
    switch (type) {
      case 'api':
        return (
          <TextField
            styleName="source__edit"
            id="url"
            regexp={URL_REGEXP}
            error={!!errors.url}
            placeholder="API URL"
            helperText={errors ? errors.url : ''}
            value={value ? value.url : ''}
            onChange={handleChangeValue}
          />
        );
      case 'corezoidSyncApi':
        return (
          <>
            <TextField
              styleName="source__edit"
              id="convId"
              type="int"
              error={!!errors.convId}
              placeholder="Process ID"
              helperText={errors ? errors.convId : ''}
              value={value ? value.convId : ''}
              onChange={handleChangeValue}
            />
            <TextField
              styleName="source__edit"
              id="apiLogin"
              type="int"
              error={!!errors.apiLogin}
              placeholder="API Login"
              helperText={errors ? errors.apiLogin : ''}
              value={value ? value.apiLogin : ''}
              onChange={handleChangeValue}
            />
            <TextField
              styleName="source__edit"
              id="apiSecret"
              type="password"
              error={!!errors.apiSecret}
              placeholder="API Secret"
              helperText={errors ? errors.apiSecret : ''}
              value={value ? value.apiSecret : ''}
              onChange={handleChangeValue}
            />
          </>
        );
      case 'refBook':
        return (
          <div styleName="source__select">
            <SelectRefBook
              id="id"
              bordered={true}
              placeholder={t(mes.selectFromList)}
              value={valueTitle}
              error={!!errors.id}
              helperText={errors ? errors.id : ''}
              onChange={({ value: refBook }) => {
                setValueTitle(refBook);
                handleChangeValue({ id: 'id', value: refBook.id });
              }}
            />
          </div>
        );
      case 'layer':
        return (
          <div styleName="source__select">
            <SelectLayer
              id="id"
              bordered={true}
              placeholder={t(mes.selectFromList)}
              value={valueTitle}
              error={!!errors.id}
              helperText={errors ? errors.id : ''}
              onChange={({ value: layer }) => {
                setValueTitle(layer);
                handleChangeValue({ id: 'id', value: layer.id });
              }}
            />
          </div>
        );
      case 'actors':
        return (
          <div styleName="source__select">
            <SelectActors
              id="ids"
              bordered={true}
              placeholder={t(mes.selectFromList)}
              value={arrValueTitle}
              exclude={arrValueTitle}
              error={!!errors.id}
              helperText={errors ? errors.id : ''}
              onChange={({ value: actors }) => {
                setArrValueTitle(actors);
                const valA = actors.map((i) => i.id);
                handleChangeValue({ id: 'ids', value: valA });
              }}
            />
          </div>
        );
      case 'actorFilter':
        return (
          <div styleName="source__select">
            <SelectActorFilters
              id="id"
              bordered={true}
              placeholder={t(mes.selectFromList)}
              value={valueTitle}
              error={!!errors.id}
              helperText={errors ? errors.id : ''}
              onChange={({ value: filter }) => {
                setValueTitle(filter);
                handleChangeValue({ id: 'id', value: filter.id });
              }}
            />
            <ActorsFiltersPopup
              value={{}}
              onChange={(filter) => {
                setValueTitle(filter);
                handleChangeValue({ id: 'id', value: filter.id });
              }}
              panelMode={false}
              type="modal"
              btns={['saveSelect']}
            >
              <Button
                type="text"
                size="small"
                fontWeight="normal"
                icon="add"
                label={t(mes.createNewFilter)}
              />
            </ActorsFiltersPopup>
          </div>
        );
      default:
        return null;
    }
  };

  const renderRadio = () => {
    let rd;
    if (isActive) {
      rd = SOURCES.map((item) => (
        <RadioItem key={item.value} label={item.title} value={item.value} />
      ));
    } else {
      const findItem = SOURCES.find(
        (item) => item.value === optionsSource.type
      );
      rd = (
        <RadioItem
          key={findItem.value}
          label={findItem.title}
          value={findItem.value}
        />
      );
    }
    return (
      <Radio
        styleName="source__radio"
        align="horizontal"
        value={optionsSource.type}
        onChange={handleSourceTypeChange}
      >
        {rd}
      </Radio>
    );
  };

  return (
    <div styleName="source">
      <Label fontWeight="semibold" value={t(mes.selectOptionsSource)} />
      {renderRadio()}
      {renderSourceSettings()}
    </div>
  );
}

OptionsSource.propTypes = {
  id: PropTypes.string,
  isActive: PropTypes.bool,
  errors: PropTypes.object,
  extra: PropTypes.object,
  onChange: PropTypes.func,
  handleInitOptions: PropTypes.func,
};

export default OptionsSource;
